/**
 * Display formatters used across V2 admin screens.
 *
 * COL-478: every formatter takes an optional `language`. Omitted, they keep
 * their historical locale (en-DE money, en-GB dates) so existing admin
 * screens do not move; tenant surfaces pass the tenant's language and get
 * `localeFor(language)`.
 */
import { localeFor, type SupportedLanguage } from "@/lib/i18n/languages";

const DEFAULT_MONEY_LOCALE = "en-DE";
const DEFAULT_DATE_LOCALE = "en-GB";

const eurFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

function eurFormatter(locale: string): Intl.NumberFormat {
  let f = eurFormatters.get(locale);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    });
    eurFormatters.set(locale, f);
  }
  return f;
}

function dateFormatter(locale: string): Intl.DateTimeFormat {
  let f = dateFormatters.get(locale);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    dateFormatters.set(locale, f);
  }
  return f;
}

function dateTimeFormatter(locale: string): Intl.DateTimeFormat {
  let f = dateTimeFormatters.get(locale);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    dateTimeFormatters.set(locale, f);
  }
  return f;
}

export function formatEUR(
  value: number | string | null | undefined,
  language?: SupportedLanguage
): string | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  return eurFormatter(
    language ? localeFor(language) : DEFAULT_MONEY_LOCALE
  ).format(n);
}

export function formatDate(
  value: string | null | undefined,
  language?: SupportedLanguage
): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return dateFormatter(
    language ? localeFor(language) : DEFAULT_DATE_LOCALE
  ).format(d);
}

export function formatDateTime(
  value: string | null | undefined,
  language?: SupportedLanguage
): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return dateTimeFormatter(
    language ? localeFor(language) : DEFAULT_DATE_LOCALE
  ).format(d);
}

/**
 * One-line address with the postal code where it belongs — next to the town:
 * "Hofmairstraße 37, 82327 Tutzing". Any missing part is simply dropped, so a
 * property with no zip still reads "Hofmairstraße 37, Tutzing".
 */
export function formatAddress(
  address: string | null | undefined,
  zip: string | null | undefined,
  city: string | null | undefined,
  separator = ", "
): string {
  const town = [zip, city]
    .map((v) => v?.toString().trim())
    .filter(Boolean)
    .join(" ");
  return [address?.trim(), town].filter(Boolean).join(separator);
}

export function maskIban(iban: string | null | undefined): string {
  if (!iban) return "—";
  if (iban.length < 9) return iban;
  return `${iban.slice(0, 4)}…${iban.slice(-4)}`;
}

/** Up-to-two-letter initials from a person or company name (for avatars). */
export function initialsFromName(name: string | null | undefined): string {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
